import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { useStrategy } from '../StrategyContext';
import Tip from '../components/Tip';

interface BacktestResult {
  id: string;
  params: any;
  total_return: number | null;
  win_rate: number | null;
  profit_factor: number | null;
  max_drawdown: number | null;
  sharpe_ratio: number | null;
  total_trades: number | null;
  equity_curve: any;
  per_coin: any;
  trade_timeline?: Array<{ symbol: string; mode: string; entry_date: string; exit_date: string; pnl_pct: number; win: boolean }>;
  created_at: string;
}

interface BacktestProps {
  isAdmin?: boolean;
}

const DEFAULTS = {
  atrMult: 4.0,
  volumeMult: 1.5,
  longLeverage: 1.0,
  pyramidTrigger: 15,
  riskPerTrade: 2.0,
  bullFilter: 'off',
  bullAdxThreshold: 25,
  bullSupertrendMult: 3.0,
  bearFilter: 'death_cross',
  bearAdxThreshold: 25,
  bearSupertrendMult: 3.0,
  shortAtrMult: 2.0,
  shortVolumeMult: 2.0,
  shortLeverage: 1.0,
  period: 'full',
  btMode: 'futures-combined',
};

const MODE_LABELS: Record<string, string> = {
  combined: 'Combined',
  'futures-combined': 'Futures Combined',
  spot: 'Spot Long',
  short: 'Short Only',
  'futures-long': 'Futures Long',
  'futures-short': 'Futures Short',
};

const SIGNAL_METHODS = [
  { value: '', label: 'None' },
  { value: 'smc', label: 'SMC' },
  { value: 'priceaction', label: 'Price Action' },
  { value: 'technical', label: 'Technical' },
];

const STRATEGY_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  donchian: { bg: 'rgba(59,130,246,0.15)', fg: '#3b82f6', label: 'Donchian' },
  ualgo: { bg: 'rgba(168,85,247,0.15)', fg: '#a855f7', label: 'UAlgo' },
  smc: { bg: 'rgba(249,115,22,0.15)', fg: '#f97316', label: 'SMC' },
  priceaction: { bg: 'rgba(6,182,212,0.15)', fg: '#06b6d4', label: 'PriceAction' },
  technical: { bg: 'rgba(239,68,68,0.15)', fg: '#ef4444', label: 'Technical' },
};

const getStrategyBadge = (params: any) => {
  const sm = params?.signal_method;
  if (sm && STRATEGY_BADGE[sm]) return STRATEGY_BADGE[sm];
  const s = params?.strategy || 'donchian';
  return STRATEGY_BADGE[s] || STRATEGY_BADGE.donchian;
};

const BULL_FILTER_LABELS: Record<string, string> = {
  sma200: 'SMA200', golden_cross: 'GoldenX', adx_dmi: 'ADX+DMI',
  supertrend: 'Supertrend', sar: 'SAR', adx_sma200: 'ADX+SMA', off: 'OFF',
};

const BEAR_FILTER_LABELS: Record<string, string> = {
  death_cross: 'DeathX', sma200: 'SMA200', adx_dmi: 'ADX+DMI',
  supertrend: 'Supertrend', sar: 'SAR', adx_sma200: 'ADX+SMA', off: 'OFF',
};

const PERIOD_LABELS: Record<string, string> = {
  full: 'Full (2022-2026)',
  train: 'Train (2022-2024)',
  test: 'Test (2025-2026)',
  custom: 'Custom',
};

const UALGO_DEFAULTS = {
  uSlAtrMult: 1.5, uFlipWindow: 7, uStMult: 1.5, uStAtrPeriod: 14,
  uRsiPeriod: 9, uRsiBuy: 65, uRsiSell: 40, uCmoBuy: -20, uCmoSell: 5,
  uTp1: 0.30, uTp2: 0.20, uMaxHold: 25, uRiskPerTrade: 2.5,
};

const shortId = (id: string): string => id ? `BT-${id.slice(-6).toUpperCase()}` : '';

const formatPeriodLabel = (params: any): string => {
  if (params?.period === 'custom' && params?.start_date && params?.end_date) {
    return `${params.start_date} to ${params.end_date}`;
  }
  return PERIOD_LABELS[params?.period] || params?.period || 'Full';
};

const Backtest: React.FC<BacktestProps> = ({ isAdmin = false }) => {
  const { strategy } = useStrategy();
  const [atrMult, setAtrMult] = useState(DEFAULTS.atrMult);
  const [volumeMult, setVolumeMult] = useState(DEFAULTS.volumeMult);
  const [pyramidTrigger, setPyramidTrigger] = useState(DEFAULTS.pyramidTrigger);
  const [riskPerTrade, setRiskPerTrade] = useState(DEFAULTS.riskPerTrade);
  const [longLeverage, setLongLeverage] = useState(DEFAULTS.longLeverage);
  const [bullFilter, setBullFilter] = useState(DEFAULTS.bullFilter);
  const [bullAdxThreshold, setBullAdxThreshold] = useState(DEFAULTS.bullAdxThreshold);
  const [bullSupertrendMult, setBullSupertrendMult] = useState(DEFAULTS.bullSupertrendMult);
  const [bearFilter, setBearFilter] = useState(DEFAULTS.bearFilter);
  const [bearAdxThreshold, setBearAdxThreshold] = useState(DEFAULTS.bearAdxThreshold);
  const [bearSupertrendMult, setBearSupertrendMult] = useState(DEFAULTS.bearSupertrendMult);
  const [shortAtrMult, setShortAtrMult] = useState(DEFAULTS.shortAtrMult);
  const [shortVolumeMult, setShortVolumeMult] = useState(DEFAULTS.shortVolumeMult);
  const [shortLeverage, setShortLeverage] = useState(DEFAULTS.shortLeverage);
  const [period, setPeriod] = useState(DEFAULTS.period);
  const [startDate, setStartDate] = useState('2022-01-01');
  const [endDate, setEndDate] = useState('2026-03-03');
  const [btMode, setBtMode] = useState(DEFAULTS.btMode);
  // UAlgo params (AI-optimized defaults)
  const [uSlAtrMult, setUSlAtrMult] = useState(UALGO_DEFAULTS.uSlAtrMult);
  const [uFlipWindow, setUFlipWindow] = useState(UALGO_DEFAULTS.uFlipWindow);
  const [uStMult, setUStMult] = useState(UALGO_DEFAULTS.uStMult);
  const [uStAtrPeriod, setUStAtrPeriod] = useState(UALGO_DEFAULTS.uStAtrPeriod);
  const [uRsiPeriod, setURsiPeriod] = useState(UALGO_DEFAULTS.uRsiPeriod);
  const [uRsiBuy, setURsiBuy] = useState(UALGO_DEFAULTS.uRsiBuy);
  const [uRsiSell, setURsiSell] = useState(UALGO_DEFAULTS.uRsiSell);
  const [uCmoBuy, setUCmoBuy] = useState(UALGO_DEFAULTS.uCmoBuy);
  const [uCmoSell, setUCmoSell] = useState(UALGO_DEFAULTS.uCmoSell);
  const [uTp1, setUTp1] = useState(UALGO_DEFAULTS.uTp1);
  const [uTp2, setUTp2] = useState(UALGO_DEFAULTS.uTp2);
  const [uMaxHold, setUMaxHold] = useState(UALGO_DEFAULTS.uMaxHold);
  const [uRiskPerTrade, setURiskPerTrade] = useState(UALGO_DEFAULTS.uRiskPerTrade);
  // Signal method params
  const [signalMethod, setSignalMethod] = useState('');
  const [sigTimeframe, setSigTimeframe] = useState('1d');
  const [sigConfGate, setSigConfGate] = useState(65);
  const [smcObProx, setSmcObProx] = useState(1.5);
  const [smcAdx, setSmcAdx] = useState(25);
  const [smcSlAtr, setSmcSlAtr] = useState(1.5);
  const [paCluster, setPaCluster] = useState(0.8);
  const [paAdx, setPaAdx] = useState(25);
  const [paSlAtr, setPaSlAtr] = useState(1.5);
  const [paDistGate, setPaDistGate] = useState(7.0);
  const [techSlAtr, setTechSlAtr] = useState(1.5);
  const [techTpAtr, setTechTpAtr] = useState(2.0);
  const isSignalMethod = signalMethod !== '';
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<BacktestResult[]>([]);
  const [active, setActive] = useState<BacktestResult | null>(null);
  const [page, setPage] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const PAGE_SIZE = 20;
  const isUAlgo = strategy === 'ualgo' && !isSignalMethod;

  const loadResults = async (p = 0) => {
    const resp = await api.getBacktestResults(PAGE_SIZE, p * PAGE_SIZE);
    setResults(resp.data || resp);
    setTotalResults(resp.total ?? (resp.data || resp).length);
    setPage(p);
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadResults(0).catch(console.error);
  }, [isAdmin]);

  // Show the most recent completed result by default
  useEffect(() => {
    const completed = results.find(r => r.total_return != null);
    if (completed && !active) setActive(completed);
  }, [results, active]);

  // Auto-set optimal filters + mode when switching to UAlgo
  useEffect(() => {
    if (isUAlgo) {
      setBullFilter('adx_dmi');
      setBearFilter('off');
      setBtMode('futures-combined');
    } else if (!isSignalMethod) {
      setBullFilter(DEFAULTS.bullFilter);
      setBearFilter(DEFAULTS.bearFilter);
      setBtMode(DEFAULTS.btMode);
    }
  }, [isUAlgo]);

  const SIG_DEFAULTS = {
    sigTimeframe: '1d', sigConfGate: 65, smcObProx: 1.5, smcAdx: 25, smcSlAtr: 1.5,
    paCluster: 0.8, paAdx: 25, paSlAtr: 1.5, paDistGate: 7.0,
    techSlAtr: 1.5, techTpAtr: 2.0,
  };

  const handleReset = () => {
    if (isSignalMethod) {
      setSigTimeframe(SIG_DEFAULTS.sigTimeframe);
      setSigConfGate(SIG_DEFAULTS.sigConfGate);
      setSmcObProx(SIG_DEFAULTS.smcObProx); setSmcAdx(SIG_DEFAULTS.smcAdx); setSmcSlAtr(SIG_DEFAULTS.smcSlAtr);
      setPaCluster(SIG_DEFAULTS.paCluster); setPaAdx(SIG_DEFAULTS.paAdx); setPaSlAtr(SIG_DEFAULTS.paSlAtr); setPaDistGate(SIG_DEFAULTS.paDistGate);
      setTechSlAtr(SIG_DEFAULTS.techSlAtr); setTechTpAtr(SIG_DEFAULTS.techTpAtr);
    } else if (isUAlgo) {
      setUSlAtrMult(UALGO_DEFAULTS.uSlAtrMult); setUFlipWindow(UALGO_DEFAULTS.uFlipWindow);
      setUStMult(UALGO_DEFAULTS.uStMult); setUStAtrPeriod(UALGO_DEFAULTS.uStAtrPeriod);
      setURsiPeriod(UALGO_DEFAULTS.uRsiPeriod); setURsiBuy(UALGO_DEFAULTS.uRsiBuy);
      setURsiSell(UALGO_DEFAULTS.uRsiSell); setUCmoBuy(UALGO_DEFAULTS.uCmoBuy);
      setUCmoSell(UALGO_DEFAULTS.uCmoSell); setUTp1(UALGO_DEFAULTS.uTp1);
      setUTp2(UALGO_DEFAULTS.uTp2); setUMaxHold(UALGO_DEFAULTS.uMaxHold);
      setURiskPerTrade(UALGO_DEFAULTS.uRiskPerTrade);
      setBullFilter('adx_dmi'); setBearFilter('off');
    } else {
      setAtrMult(DEFAULTS.atrMult); setVolumeMult(DEFAULTS.volumeMult);
      setPyramidTrigger(DEFAULTS.pyramidTrigger); setRiskPerTrade(DEFAULTS.riskPerTrade);
      setLongLeverage(DEFAULTS.longLeverage);
      setBullFilter(DEFAULTS.bullFilter); setBullAdxThreshold(DEFAULTS.bullAdxThreshold);
      setBullSupertrendMult(DEFAULTS.bullSupertrendMult);
      setBearFilter(DEFAULTS.bearFilter); setBearAdxThreshold(DEFAULTS.bearAdxThreshold);
      setBearSupertrendMult(DEFAULTS.bearSupertrendMult);
      setShortAtrMult(DEFAULTS.shortAtrMult); setShortVolumeMult(DEFAULTS.shortVolumeMult);
      setShortLeverage(DEFAULTS.shortLeverage);
    }
    setPeriod(DEFAULTS.period);
    setBtMode(isUAlgo ? 'futures-combined' : DEFAULTS.btMode);
    setStartDate('2022-01-01'); setEndDate('2026-03-03');
  };

  const showShortParams = btMode === 'combined' || btMode === 'futures-combined' || btMode === 'futures-short';
  const showLongParams = btMode !== 'futures-short';

  const isDefault = isUAlgo
    ? (uSlAtrMult === UALGO_DEFAULTS.uSlAtrMult && uFlipWindow === UALGO_DEFAULTS.uFlipWindow &&
      uStMult === UALGO_DEFAULTS.uStMult && uStAtrPeriod === UALGO_DEFAULTS.uStAtrPeriod &&
      uRsiPeriod === UALGO_DEFAULTS.uRsiPeriod && uRsiBuy === UALGO_DEFAULTS.uRsiBuy &&
      uRsiSell === UALGO_DEFAULTS.uRsiSell && uCmoBuy === UALGO_DEFAULTS.uCmoBuy &&
      uCmoSell === UALGO_DEFAULTS.uCmoSell && uTp1 === UALGO_DEFAULTS.uTp1 &&
      uTp2 === UALGO_DEFAULTS.uTp2 && uMaxHold === UALGO_DEFAULTS.uMaxHold &&
      uRiskPerTrade === UALGO_DEFAULTS.uRiskPerTrade &&
      period === DEFAULTS.period && btMode === 'futures-combined')
    : (atrMult === DEFAULTS.atrMult && volumeMult === DEFAULTS.volumeMult &&
      pyramidTrigger === DEFAULTS.pyramidTrigger && riskPerTrade === DEFAULTS.riskPerTrade &&
      longLeverage === DEFAULTS.longLeverage &&
      bullFilter === DEFAULTS.bullFilter && bullAdxThreshold === DEFAULTS.bullAdxThreshold &&
      bullSupertrendMult === DEFAULTS.bullSupertrendMult &&
      bearFilter === DEFAULTS.bearFilter && bearAdxThreshold === DEFAULTS.bearAdxThreshold &&
      bearSupertrendMult === DEFAULTS.bearSupertrendMult &&
      shortAtrMult === DEFAULTS.shortAtrMult && shortVolumeMult === DEFAULTS.shortVolumeMult &&
      shortLeverage === DEFAULTS.shortLeverage &&
      period === DEFAULTS.period && btMode === DEFAULTS.btMode);

  const handleRun = async () => {
    setRunning(true);
    setError('');
    try {
      const res = await api.runBacktest({
        strategy,
        period,
        mode: btMode,
        ...(period === 'custom' ? { start_date: startDate, end_date: endDate } : {}),
        // Donchian-specific params (only when not signal method or ualgo)
        ...(!isSignalMethod && !isUAlgo ? {
          atr_mult: atrMult,
          volume_mult: volumeMult,
          pyramid_trigger: pyramidTrigger,
          risk_per_trade: riskPerTrade,
          long_leverage: longLeverage,
          bull_filter: bullFilter,
          bull_adx_threshold: bullAdxThreshold,
          bull_supertrend_mult: bullSupertrendMult,
          bear_filter: bearFilter,
          bear_adx_threshold: bearAdxThreshold,
          bear_supertrend_mult: bearSupertrendMult,
          short_atr_mult: shortAtrMult,
          short_volume_mult: shortVolumeMult,
          short_leverage: shortLeverage,
        } : {}),
        // UAlgo params
        ...(isUAlgo ? {
          sl_atr_mult: uSlAtrMult, flip_window: uFlipWindow,
          supertrend_multiplier: uStMult, supertrend_atr_period: uStAtrPeriod,
          rsi_period: uRsiPeriod, rsi_buy_threshold: uRsiBuy, rsi_sell_threshold: uRsiSell,
          cmo_buy_threshold: uCmoBuy, cmo_sell_threshold: uCmoSell,
          tp1_fraction: uTp1, tp2_fraction: uTp2, tp3_fraction: 1 - uTp1 - uTp2,
          max_hold_days: uMaxHold,
          risk_per_trade: uRiskPerTrade,
          bull_filter: bullFilter,
          bear_filter: bearFilter,
          long_leverage: longLeverage,
          short_leverage: shortLeverage,
        } : {}),
        // Signal method params
        ...(isSignalMethod ? {
          signal_method: signalMethod,
          signal_timeframe: sigTimeframe,
          signal_confidence_gate: sigConfGate,
          ...(signalMethod === 'smc' ? { smc_ob_proximity_pct: smcObProx, smc_adx_threshold: smcAdx, smc_sl_atr_mult: smcSlAtr } : {}),
          ...(signalMethod === 'priceaction' ? { pa_sr_cluster_pct: paCluster, pa_adx_threshold: paAdx, pa_sl_atr_mult: paSlAtr, pa_distance_gate_pct: paDistGate } : {}),
          ...(signalMethod === 'technical' ? { tech_sl_atr_mult: techSlAtr, tech_tp_atr_mult: techTpAtr } : {}),
        } : {}),
      });
      if (res.total_return != null) {
        setActive({
          id: res.id,
          params: { strategy, period, mode: btMode, ...(period === 'custom' ? { start_date: startDate, end_date: endDate } : {}), ...(!isSignalMethod && !isUAlgo ? { atr_mult: atrMult, volume_mult: volumeMult, long_leverage: longLeverage, bull_filter: bullFilter, bear_filter: bearFilter, pyramid_trigger: pyramidTrigger, risk_per_trade: riskPerTrade, short_atr_mult: shortAtrMult, short_volume_mult: shortVolumeMult, short_leverage: shortLeverage } : {}), ...(isUAlgo ? { supertrend_multiplier: uStMult, sl_atr_mult: uSlAtrMult, flip_window: uFlipWindow, rsi_buy_threshold: uRsiBuy, rsi_sell_threshold: uRsiSell, cmo_buy_threshold: uCmoBuy, cmo_sell_threshold: uCmoSell, risk_per_trade: uRiskPerTrade, bull_filter: bullFilter, bear_filter: bearFilter } : {}), ...(isSignalMethod ? { signal_method: signalMethod, signal_timeframe: sigTimeframe, signal_confidence_gate: sigConfGate } : {}) },
          total_return: res.total_return,
          win_rate: res.win_rate,
          profit_factor: res.profit_factor,
          max_drawdown: res.max_drawdown,
          sharpe_ratio: res.sharpe_ratio,
          total_trades: res.total_trades,
          equity_curve: res.equity_curve,
          per_coin: res.per_coin,
          trade_timeline: res.trade_timeline,
          created_at: new Date().toISOString(),
        });
      }
      await loadResults(0);
    } catch (err: any) {
      console.error('Backtest error:', err);
      setError(err?.message || 'Backtest failed. Please try again.');
    } finally {
      setRunning(false);
    }
  };

  // Access denied for non-admin
  if (!isAdmin) {
    return (
      <div>
        <div className="page-header">
          <div className="page-title">Backtester</div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="empty-state">
              <div className="empty-state-icon">&#128274;</div>
              <div className="empty-state-text">Admin Access Required</div>
              <div className="empty-state-sub">
                The backtester is currently available to administrators only.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const r = active;
  const hasResults = r && r.total_return != null;
  const perCoin = r?.per_coin || [];

  // Build equity curve SVG from result
  const buildSvg = () => {
    const curve = r?.equity_curve;
    if (!curve || !Array.isArray(curve) || curve.length < 2) {
      return { line: 'M0,200 L600,38', area: 'M0,200 L600,38 L600,240 L0,240 Z' };
    }
    const vals = curve.map((p: any) => typeof p === 'number' ? p : p.equity || p.value || 0);
    const min = Math.min(...vals) * 0.98;
    const max = Math.max(...vals) * 1.02;
    const range = max - min || 1;
    const points = vals.map((v: number, i: number) => {
      const x = (i / (vals.length - 1)) * 600;
      const y = 240 - ((v - min) / range) * 200;
      return `${x},${y}`;
    });
    const line = 'M' + points.join(' L');
    const area = line + ' L600,240 L0,240 Z';
    return { line, area };
  };
  const svg = buildSvg();

  // Compute max for bar scaling
  const maxCoinPct = Math.max(...(perCoin.map((c: any) => Math.abs(c.total_pnl_pct || c.pct || 0))), 1);

  // Interpret results
  const getReturnGrade = (ret: number) => {
    if (ret >= 100) return { label: 'Excellent', color: '#22c55e' };
    if (ret >= 50) return { label: 'Good', color: '#22c55e' };
    if (ret >= 20) return { label: 'Moderate', color: '#eab308' };
    if (ret >= 0) return { label: 'Low', color: '#eab308' };
    return { label: 'Negative', color: '#ef4444' };
  };

  const getPfGrade = (pf: number) => {
    if (pf >= 2.0) return 'Excellent';
    if (pf >= 1.5) return 'Good';
    if (pf >= 1.0) return 'Marginal';
    return 'Losing';
  };

  const getDdGrade = (dd: number) => {
    if (dd <= 10) return 'Conservative';
    if (dd <= 15) return 'Moderate';
    if (dd <= 25) return 'Aggressive';
    return 'High Risk';
  };

  // Filter completed results only for Previous Backtests table
  const completedResults = results.filter(r => r.total_return != null);

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Backtester</div>
        <div className="header-actions">
          {!isDefault && (
            <button className="btn" onClick={handleReset}>Reset Defaults</button>
          )}
        </div>
      </div>

      {/* Strategy type selector + description */}
      <div className="flex items-center gap-3 mb-4 px-3 py-2 bg-blue-500/5 border border-blue-500/10 rounded-lg" style={{ flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span className="text-xs text-gray-500">Strategy:</span>
          {[
            { val: '', label: 'Donchian', color: '#3b82f6' },
            { val: 'ualgo', label: 'UAlgo', color: '#a855f7' },
            { val: 'smc', label: 'SMC', color: '#f97316' },
            { val: 'priceaction', label: 'PriceAction', color: '#06b6d4' },
            { val: 'technical', label: 'Technical', color: '#ef4444' },
          ].map(s => {
            const active = s.val === '' ? (!isUAlgo && !isSignalMethod) : (s.val === 'ualgo' ? isUAlgo : signalMethod === s.val);
            return (
              <button key={s.val} className="badge" onClick={() => {
                if (s.val === '') { setSignalMethod(''); }
                else if (s.val === 'ualgo') { setSignalMethod(''); }
                else { setSignalMethod(s.val); }
              }} style={{
                fontSize: 10, padding: '2px 8px', cursor: 'pointer', border: 'none',
                background: active ? `${s.color}22` : 'transparent',
                color: active ? s.color : '#71717a',
                outline: active ? `1px solid ${s.color}44` : '1px solid transparent',
              }}>{s.label}</button>
            );
          })}
        </div>
        <span className="text-xs text-gray-400" style={{ flex: 1 }}>
          {isSignalMethod
            ? `${signalMethod.toUpperCase()} signal method backtest. Runs ${signalMethod === 'smc' ? 'Smart Money Concepts (OBs, FVGs, BOS)' : signalMethod === 'technical' ? 'Technical Analysis (RSI, MACD, BB, Stoch RSI, CCI, Williams %R, ADX)' : 'Price Action (S/R clusters, swing structure)'} on ${sigTimeframe} candles across 8 coins. Entries use signal SL/TP, max 4 positions, 2% risk per trade.`
            : isUAlgo
            ? 'UAlgo Trend strategy with Supertrend + RSI + CMO signals, fixed stop loss, and R-multiple take profits (1R/2R/3R). Daily candles, 8 coins, max 4 positions.'
            : 'Donchian Channel Breakout strategy with ATR trailing stops, partial profit taking, and BTC regime filters. Runs on daily candles across 8 coins with max 4 concurrent positions.'}
        </span>
      </div>

      {/* Parameters */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <div className="card-title">Parameters</div>
        </div>
        <div className="card-body">
          {/* Row 1: Mode + Period + Filters */}
          <div style={{ display: 'grid', gridTemplateColumns: period === 'custom' ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div className="form-group">
              <label className="form-label">Mode <Tip text={isSignalMethod ? "Spot = long signals only. Short = short signals only. Combined = both buy and sell signals." : "Spot = long-only via Coinbase spot (higher fees). Futures Long = long via perpetual futures (lower fees). Futures Short = short selling via perps (bear filter). Combined = Futures Long + Short sharing a 4-position pool."} /></label>
              <select className="form-input" value={btMode} onChange={e => setBtMode(e.target.value)}>
                {isSignalMethod ? (
                  <>
                    <option value="combined">Combined (Long + Short)</option>
                    <option value="spot">Long Only</option>
                    <option value="short">Short Only</option>
                  </>
                ) : (
                  <>
                    <option value="futures-combined">Futures Combined (Long + Short)</option>
                    <option value="combined">All Modes (Spot + Futures)</option>
                    <option value="spot">Spot (Long Only)</option>
                    <option value="futures-long">Futures Long</option>
                    <option value="futures-short">Futures Short</option>
                  </>
                )}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Period <Tip text="Full = 4-year backtest (2022-2026). Train = in-sample period (2022-2024) for optimization. Test = out-of-sample (2025-2026) for validation. Custom = pick your own date range." /></label>
              <select className="form-input" value={period} onChange={e => setPeriod(e.target.value)}>
                <option value="full">Full (2022-2026)</option>
                <option value="train">Train (2022-2024)</option>
                <option value="test">Test (2025-2026)</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
            {showLongParams && !isSignalMethod ? (
              <div className="form-group">
                <label className="form-label">Bull Filter <Tip text="Gates long entries. SMA200 = BTC > SMA(200) (default). Golden Cross = SMA50 > SMA200 AND close > SMA200 (strict). ADX+DMI = trend strength + direction. Supertrend = ATR-adaptive. SAR = Parabolic SAR." /></label>
                <select className="form-input" value={bullFilter} onChange={e => setBullFilter(e.target.value)}>
                  <option value="sma200">BTC &gt; SMA(200)</option>
                  <option value="golden_cross">Golden Cross</option>
                  <option value="adx_dmi">ADX + DMI</option>
                  <option value="supertrend">Supertrend</option>
                  <option value="sar">Parabolic SAR</option>
                  <option value="adx_sma200">ADX + SMA(200)</option>
                  <option value="off">Off (no filter)</option>
                </select>
              </div>
            ) : <div />}
            {showShortParams && !isSignalMethod ? (
              <div className="form-group">
                <label className="form-label">Bear Filter <Tip text="Gates short entries. Death Cross = strictest (~22% of days). SMA200 = relaxed (~28%). ADX+DMI = trend strength + direction. Supertrend = ATR-adaptive trend. SAR = Parabolic SAR. ADX+SMA200 = combined." /></label>
                <select className="form-input" value={bearFilter} onChange={e => setBearFilter(e.target.value)}>
                  <option value="death_cross">Death Cross</option>
                  <option value="sma200">BTC &lt; SMA(200)</option>
                  <option value="adx_dmi">ADX + DMI</option>
                  <option value="supertrend">Supertrend</option>
                  <option value="sar">Parabolic SAR</option>
                  <option value="adx_sma200">ADX + SMA(200)</option>
                  <option value="off">Off (no filter)</option>
                </select>
              </div>
            ) : <div />}
          </div>
          {period === 'custom' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input type="date" className="form-input" value={startDate} onChange={e => setStartDate(e.target.value)} min="2020-01-01" max={endDate} />
              </div>
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input type="date" className="form-input" value={endDate} onChange={e => setEndDate(e.target.value)} min={startDate} max="2026-12-31" />
              </div>
              <div /><div />
            </div>
          )}

          {/* Donchian-specific params */}
          {!isUAlgo && showLongParams && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Long ATR <Tip text="Long trailing stop distance = ATR(14) × this value. Higher = wider stops, fewer whipsaws but larger losses per trade. Production uses 4.0x." /></label>
                <input type="number" className="form-input" value={atrMult} onChange={e => setAtrMult(Number(e.target.value))} step="0.5" min="1" max="10" />
              </div>
              <div className="form-group">
                <label className="form-label">Long Volume <Tip text="Long entry requires volume > this × 20-day average. Higher = fewer but higher-quality signals. 0 = disabled. Production uses 1.5x." /></label>
                <input type="number" className="form-input" value={volumeMult} onChange={e => setVolumeMult(Number(e.target.value))} step="0.1" min="0" max="5" />
              </div>
              <div className="form-group">
                <label className="form-label">Long Leverage <Tip text="Leverage multiplier for futures long positions. Only applies to futures longs (spot is always 1x). Max 3.0x. Production uses 1.0x." /></label>
                <input type="number" className="form-input" value={longLeverage} onChange={e => setLongLeverage(Number(e.target.value))} step="0.5" min="1" max="3" />
              </div>
            </div>
          )}
          {showLongParams && (bullFilter === 'adx_dmi' || bullFilter === 'adx_sma200') && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16, marginTop: -8 }}>
              <div className="form-group">
                <label className="form-label">Bull ADX Threshold <Tip text="Minimum ADX value to confirm uptrend strength. 25 = standard, 20 = aggressive (more trades), 30 = conservative (fewer, stronger trends)." /></label>
                <input type="number" className="form-input" value={bullAdxThreshold} onChange={e => setBullAdxThreshold(Number(e.target.value))} step="5" min="15" max="40" />
              </div>
              <div /><div />
            </div>
          )}
          {showLongParams && bullFilter === 'supertrend' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16, marginTop: -8 }}>
              <div className="form-group">
                <label className="form-label">Bull Supertrend Mult <Tip text="ATR multiplier for Supertrend bands. Lower = more sensitive (more trades, more whipsaws). Higher = smoother. Standard: 3.0." /></label>
                <input type="number" className="form-input" value={bullSupertrendMult} onChange={e => setBullSupertrendMult(Number(e.target.value))} step="0.5" min="1" max="5" />
              </div>
              <div /><div />
            </div>
          )}
          {!isUAlgo && showShortParams && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Short ATR <Tip text="Short trailing stop distance = ATR(14) × this value. Shorts use tighter stops than longs. Production uses 2.0x." /></label>
                <input type="number" className="form-input" value={shortAtrMult} onChange={e => setShortAtrMult(Number(e.target.value))} step="0.5" min="0.5" max="6" />
              </div>
              <div className="form-group">
                <label className="form-label">Short Volume <Tip text="Short entry requires volume > this × 20-day average. Shorts use stricter volume confirmation. Production uses 2.0x." /></label>
                <input type="number" className="form-input" value={shortVolumeMult} onChange={e => setShortVolumeMult(Number(e.target.value))} step="0.1" min="0" max="5" />
              </div>
              <div className="form-group">
                <label className="form-label">Short Leverage <Tip text="Leverage multiplier for short positions. Higher leverage = larger positions but more risk. Max 3.0x. Production uses 1.0x." /></label>
                <input type="number" className="form-input" value={shortLeverage} onChange={e => setShortLeverage(Number(e.target.value))} step="0.5" min="1" max="3" />
              </div>
            </div>
          )}
          {showShortParams && (bearFilter === 'adx_dmi' || bearFilter === 'adx_sma200') && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16, marginTop: -8 }}>
              <div className="form-group">
                <label className="form-label">ADX Threshold <Tip text="Minimum ADX value to confirm trend strength. Higher = only strongest trends. 25 = standard, 20 = aggressive (more trades), 30 = conservative (fewer, stronger)." /></label>
                <input type="number" className="form-input" value={bearAdxThreshold} onChange={e => setBearAdxThreshold(Number(e.target.value))} step="5" min="15" max="40" />
              </div>
              <div /><div />
            </div>
          )}
          {showShortParams && bearFilter === 'supertrend' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16, marginTop: -8 }}>
              <div className="form-group">
                <label className="form-label">Supertrend Mult <Tip text="ATR multiplier for Supertrend bands. Lower = more sensitive (more trades, more whipsaws). Higher = smoother (fewer trades, less noise). Standard: 3.0. Range: 1.0-5.0." /></label>
                <input type="number" className="form-input" value={bearSupertrendMult} onChange={e => setBearSupertrendMult(Number(e.target.value))} step="0.5" min="1" max="5" />
              </div>
              <div /><div />
            </div>
          )}
          {!isUAlgo && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Pyramid Trigger (%) <Tip text="Add to winning positions at this % gain on a new breakout. Adds 1% risk tranche. 0 = disabled. Production uses 15%." /></label>
                <input type="number" className="form-input" value={pyramidTrigger} onChange={e => setPyramidTrigger(Number(e.target.value))} step="5" min="0" max="50" />
              </div>
              <div className="form-group">
                <label className="form-label">Risk Per Trade (%) <Tip text="Percentage of portfolio equity risked per trade. Position size = (equity × risk%) / stop distance. Production uses 2%." /></label>
                <input type="number" className="form-input" value={riskPerTrade} onChange={e => setRiskPerTrade(Number(e.target.value))} step="0.5" min="0.5" max="5" />
              </div>
              <div />
            </div>
          )}

          {/* UAlgo-specific params */}
          {isUAlgo && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div className="form-group">
                  <label className="form-label">Supertrend Mult <Tip text="ATR multiplier for Supertrend indicator bands. Lower = more sensitive to price swings (more signals). Higher = smoother trend. Default: 2.0." /></label>
                  <input type="number" className="form-input" value={uStMult} onChange={e => setUStMult(Number(e.target.value))} step="0.5" min="0.5" max="5" />
                </div>
                <div className="form-group">
                  <label className="form-label">ST ATR Period <Tip text="ATR lookback period for Supertrend calculation. Longer = smoother, slower signals. Default: 14." /></label>
                  <input type="number" className="form-input" value={uStAtrPeriod} onChange={e => setUStAtrPeriod(Number(e.target.value))} step="1" min="5" max="30" />
                </div>
                <div className="form-group">
                  <label className="form-label">SL ATR Mult <Tip text="Stop loss distance = ATR(14) × this value. Adapts to market volatility. Min floor of 1% prevents tiny stops. Default: 2.0x." /></label>
                  <input type="number" className="form-input" value={uSlAtrMult} onChange={e => setUSlAtrMult(Number(e.target.value))} step="0.5" min="0.5" max="10" />
                </div>
                <div className="form-group">
                  <label className="form-label">Flip Window <Tip text="Max bars since Supertrend flip for entry. Entry only valid if Supertrend flipped within this many bars. Default: 5." /></label>
                  <input type="number" className="form-input" value={uFlipWindow} onChange={e => setUFlipWindow(Number(e.target.value))} step="1" min="1" max="15" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div className="form-group">
                  <label className="form-label">RSI Period <Tip text="RSI lookback period. Default: 9 (shorter = more responsive)." /></label>
                  <input type="number" className="form-input" value={uRsiPeriod} onChange={e => setURsiPeriod(Number(e.target.value))} step="1" min="3" max="21" />
                </div>
                <div className="form-group">
                  <label className="form-label">RSI Buy &lt; <Tip text="Long entry requires RSI below this value (not overbought). Default: 60." /></label>
                  <input type="number" className="form-input" value={uRsiBuy} onChange={e => setURsiBuy(Number(e.target.value))} step="5" min="30" max="80" />
                </div>
                <div className="form-group">
                  <label className="form-label">RSI Sell &gt; <Tip text="Short entry requires RSI above this value (not oversold). Default: 40." /></label>
                  <input type="number" className="form-input" value={uRsiSell} onChange={e => setURsiSell(Number(e.target.value))} step="5" min="20" max="70" />
                </div>
                <div className="form-group">
                  <label className="form-label">Max Hold Days <Tip text="Maximum days to hold a position before forced exit. Default: 25." /></label>
                  <input type="number" className="form-input" value={uMaxHold} onChange={e => setUMaxHold(Number(e.target.value))} step="5" min="5" max="90" />
                </div>
                <div className="form-group">
                  <label className="form-label">Risk % <Tip text="Percentage of capital risked per trade. Higher = larger positions. Default: 2.5%." /></label>
                  <input type="number" className="form-input" value={uRiskPerTrade} onChange={e => setURiskPerTrade(Number(e.target.value))} step="0.5" min="0.5" max="5" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div className="form-group">
                  <label className="form-label">CMO Buy &gt; <Tip text="Long entry requires CMO (Chande Momentum on HMA diff) above this value. Confirms momentum direction. Default: -10." /></label>
                  <input type="number" className="form-input" value={uCmoBuy} onChange={e => setUCmoBuy(Number(e.target.value))} step="5" min={-50} max="50" />
                </div>
                <div className="form-group">
                  <label className="form-label">CMO Sell &lt; <Tip text="Short entry requires CMO below this value. Confirms bearish momentum. Default: 10." /></label>
                  <input type="number" className="form-input" value={uCmoSell} onChange={e => setUCmoSell(Number(e.target.value))} step="5" min={-50} max="50" />
                </div>
                <div className="form-group">
                  <label className="form-label">TP1 Fraction <Tip text="Fraction of position to close at 1R profit (1× stop distance). Default: 0.25 (25%)." /></label>
                  <input type="number" className="form-input" value={uTp1} onChange={e => setUTp1(Number(e.target.value))} step="0.05" min="0" max="0.5" />
                </div>
                <div className="form-group">
                  <label className="form-label">TP2 Fraction <Tip text="Fraction of position to close at 2R profit. Remainder closes at 3R. Default: 0.25 (25%). TP3 = 1 - TP1 - TP2." /></label>
                  <input type="number" className="form-input" value={uTp2} onChange={e => setUTp2(Number(e.target.value))} step="0.05" min="0" max="0.5" />
                </div>
                <div className="form-group">
                  <label className="form-label">Long Leverage <Tip text="Leverage multiplier for futures long positions. Spot longs are always 1x. Max 3.0x." /></label>
                  <input type="number" className="form-input" value={longLeverage} onChange={e => setLongLeverage(Number(e.target.value))} step="0.5" min="1" max="3" />
                </div>
                <div className="form-group">
                  <label className="form-label">Short Leverage <Tip text="Leverage multiplier for short positions. Higher leverage = larger positions but more risk. Max 3.0x." /></label>
                  <input type="number" className="form-input" value={shortLeverage} onChange={e => setShortLeverage(Number(e.target.value))} step="0.5" min="1" max="3" />
                </div>
              </div>
            </>
          )}

          {/* Signal method params */}
          {isSignalMethod && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div className="form-group">
                  <label className="form-label">Timeframe <Tip text="Candle timeframe for signal generation. 1h and 4h typically produce more signals than daily. First run fetches historical data (~30s per coin for 1h)." /></label>
                  <select className="form-input" value={sigTimeframe} onChange={e => setSigTimeframe(e.target.value)}>
                    <option value="1d">Daily (1d)</option>
                    <option value="4h">4 Hour (4h)</option>
                    <option value="1h">1 Hour (1h)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Confidence Gate <Tip text="Minimum confidence score to enter a trade. Higher = fewer but higher-quality signals. Each method has its own confidence scale." /></label>
                  <input type="number" className="form-input" value={sigConfGate} onChange={e => setSigConfGate(Number(e.target.value))} step="5" min="20" max="80" />
                </div>
                {signalMethod === 'smc' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">OB Proximity % <Tip text="How close price must be to an Order Block to trigger entry. Lower = stricter. Default: 1.5%." /></label>
                      <input type="number" className="form-input" value={smcObProx} onChange={e => setSmcObProx(Number(e.target.value))} step="0.5" min="0.5" max="5" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">ADX Threshold <Tip text="Minimum ADX value to confirm trend. Higher = only strong trends. Default: 25." /></label>
                      <input type="number" className="form-input" value={smcAdx} onChange={e => setSmcAdx(Number(e.target.value))} step="5" min="15" max="40" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">SL ATR Mult <Tip text="Stop loss distance = ATR(14) × this value. Lower = tighter stops, higher win rate but smaller wins. Default: 1.5." /></label>
                      <input type="number" className="form-input" value={smcSlAtr} onChange={e => setSmcSlAtr(Number(e.target.value))} step="0.5" min="0.5" max="4" />
                    </div>
                  </>
                )}
                {signalMethod === 'priceaction' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">S/R Cluster % <Tip text="Proximity tolerance for clustering nearby S/R levels. Higher = more levels merge together. Default: 0.8%." /></label>
                      <input type="number" className="form-input" value={paCluster} onChange={e => setPaCluster(Number(e.target.value))} step="0.1" min="0.3" max="2" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">ADX Threshold <Tip text="Minimum ADX value to confirm trend direction. Default: 25." /></label>
                      <input type="number" className="form-input" value={paAdx} onChange={e => setPaAdx(Number(e.target.value))} step="5" min="15" max="40" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Distance Gate % <Tip text="Max distance from current price to nearest S/R for entry. Lower = only enters very close to support/resistance. Default: 7%." /></label>
                      <input type="number" className="form-input" value={paDistGate} onChange={e => setPaDistGate(Number(e.target.value))} step="1" min="1" max="15" />
                    </div>
                  </>
                )}
                {signalMethod === 'technical' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">SL ATR Mult <Tip text="Stop loss distance = ATR(14) × this value. Lower = tighter stops. Default: 1.5." /></label>
                      <input type="number" className="form-input" value={techSlAtr} onChange={e => setTechSlAtr(Number(e.target.value))} step="0.5" min="0.5" max="4" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">TP ATR Mult <Tip text="Take profit distance = ATR(14) × this value. Higher = larger targets. Default: 2.0." /></label>
                      <input type="number" className="form-input" value={techTpAtr} onChange={e => setTechTpAtr(Number(e.target.value))} step="0.5" min="0.5" max="6" />
                    </div>
                  </>
                )}
              </div>
              {signalMethod === 'priceaction' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div className="form-group">
                    <label className="form-label">SL ATR Mult <Tip text="Stop loss distance below/above S/R = ATR(14) × this value. Default: 1.5." /></label>
                    <input type="number" className="form-input" value={paSlAtr} onChange={e => setPaSlAtr(Number(e.target.value))} step="0.5" min="0.5" max="4" />
                  </div>
                  <div /><div /><div />
                </div>
              )}
            </>
          )}

          {/* Run button */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <div /><div />
            <div className="form-group" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" style={{ height: 38, width: '100%' }} onClick={handleRun} disabled={running}>
                {running ? 'Running...' : 'Run Backtest'}
              </button>
            </div>
          </div>
          {running && (
            <div className="text-xs text-gray-400 mt-2">
              Fetching historical data and running strategy... this may take 30-60 seconds on first run (data is cached after).
            </div>
          )}
          {error && (
            <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      {hasResults ? (
        <>
          {/* Results Summary Banner */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-body" style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <span className="text-xs font-mono" style={{ color: '#3f3f46', marginRight: 4 }}>{shortId(r.id)}</span>
                {(() => { const b = getStrategyBadge(r.params); return (
                  <span className="badge" style={{ fontSize: 10, padding: '1px 6px', background: b.bg, color: b.fg }}>{b.label}</span>
                ); })()}
                <span className="text-xs font-mono" style={{ color: '#71717a' }}>
                  {MODE_LABELS[r.params?.mode] || 'Combined'} &middot; {formatPeriodLabel(r.params)} &middot; {r.total_trades} trades
                </span>
                {!r.params?.signal_method && r.params?.mode !== 'futures-short' && (() => {
                  const bf = r.params?.bull_filter === true ? 'sma200' : (r.params?.bull_filter || 'sma200');
                  const label = BULL_FILTER_LABELS[bf] || bf;
                  const extra = (bf === 'adx_dmi' || bf === 'adx_sma200') ? `≥${r.params?.bull_adx_threshold ?? 25}` : bf === 'supertrend' ? `×${r.params?.bull_supertrend_mult ?? 3}` : '';
                  return <span className="text-xs font-mono" style={{ color: '#60a5fa' }}>Bull: {label}{extra}</span>;
                })()}
                {!r.params?.signal_method && (r.params?.mode === 'combined' || r.params?.mode === 'futures-combined' || r.params?.mode === 'futures-short') && (() => {
                  const bf = r.params?.bear_filter || 'death_cross';
                  const label = BEAR_FILTER_LABELS[bf] || bf;
                  const extra = (bf === 'adx_dmi' || bf === 'adx_sma200') ? `≥${r.params?.bear_adx_threshold ?? 25}` : bf === 'supertrend' ? `×${r.params?.bear_supertrend_mult ?? 3}` : '';
                  return <span className="text-xs font-mono" style={{ color: '#eab308' }}>Bear: {label}{extra}</span>;
                })()}
              </div>
              <div className="text-xs" style={{ color: '#a1a1aa', lineHeight: 1.6 }}>
                {r.total_return != null && r.total_return >= 0 ? (
                  <>
                    A <strong style={{ color: getReturnGrade(r.total_return).color }}>${(10000 * (1 + r.total_return / 100)).toFixed(0)}</strong> ending
                    balance from $10,000 starting capital ({r.total_return >= 0 ? '+' : ''}{r.total_return.toFixed(1)}% net return after fees).
                  </>
                ) : (
                  <>
                    Ending balance of <strong style={{ color: '#ef4444' }}>${(10000 * (1 + (r.total_return || 0) / 100)).toFixed(0)}</strong> from
                    $10,000 starting capital ({r.total_return?.toFixed(1)}% net return after fees).
                  </>
                )}
                {' '}
                {r.profit_factor != null && (
                  <>Profit factor of {r.profit_factor.toFixed(2)} ({getPfGrade(r.profit_factor)}) with {getDdGrade(r.max_drawdown || 0).toLowerCase()} drawdown ({r.max_drawdown?.toFixed(1)}%).
                  {r.sharpe_ratio != null && <> Sharpe ratio: {r.sharpe_ratio.toFixed(2)}.</>}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="result-grid" style={{ marginBottom: 20 }}>
            <div className="result-card">
              <div className={`result-num ${(r.total_return || 0) >= 0 ? 'green' : 'red'}`}>
                {(r.total_return || 0) >= 0 ? '+' : ''}{r.total_return?.toFixed(1)}%
              </div>
              <div className="result-label">Total Return <Tip text="Net portfolio return after fees (0.45% spot / 0.06% futures per side) and slippage (0.05%). Starting capital: $10,000." /></div>
            </div>
            <div className="result-card">
              <div className="result-num">{r.win_rate?.toFixed(1)}%</div>
              <div className="result-label">Win Rate ({r.total_trades} trades) <Tip text="Percentage of completed trades that were profitable. Partial profit takes (TP1/TP2) are not counted as separate trades. Trend-following systems typically target 40-65% win rate." /></div>
            </div>
            <div className="result-card">
              <div className={`result-num ${(r.profit_factor || 0) >= 1.5 ? 'green' : ''}`}>{r.profit_factor?.toFixed(2)}</div>
              <div className="result-label">Profit Factor <Tip text="Gross profits / gross losses (dollar-weighted). Above 1.5 = good, above 2.0 = excellent. Below 1.0 = losing strategy." /></div>
            </div>
            <div className="result-card">
              <div className="result-num yellow">-{r.max_drawdown?.toFixed(1)}%</div>
              <div className="result-label">Max Drawdown <Tip text="Largest peak-to-trough decline in portfolio equity. Lower is better. Below 15% = conservative, 15-25% = moderate, above 25% = aggressive." /></div>
            </div>
          </div>

          {r.sharpe_ratio != null && (
            <div className="text-xs text-gray-500 text-right mb-2" style={{ marginTop: -12 }}>
              Sharpe Ratio: <span className="font-mono text-gray-300">{r.sharpe_ratio.toFixed(2)}</span>
              <Tip text="Risk-adjusted return (annualized). Above 1.0 = good, above 2.0 = excellent. Measures return per unit of volatility." />
            </div>
          )}

          <div className="grid-equal">
            {/* Equity Curve */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">Equity Curve <Tip text="Mark-to-market portfolio value over the backtest period. Includes unrealized P&L from open positions. Steeper slope = faster compounding." /></div>
              </div>
              <div className="card-body" style={{ padding: '0 20px 20px' }}>
                <div style={{ height: 240, position: 'relative' }}
                  onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const pct = x / rect.width;
                    const curve = r?.equity_curve;
                    if (!curve || !Array.isArray(curve) || curve.length < 2) return;
                    const idx = Math.min(Math.max(Math.round(pct * (curve.length - 1)), 0), curve.length - 1);
                    const pt = curve[idx];
                    const eq = typeof pt === 'number' ? pt : pt.equity || pt.value || 0;
                    const date = typeof pt === 'object' ? pt.date || '' : '';
                    const vals = curve.map((p: any) => typeof p === 'number' ? p : p.equity || p.value || 0);
                    const min = Math.min(...vals) * 0.98;
                    const max = Math.max(...vals) * 1.02;
                    const range = max - min || 1;
                    const dotY = 240 - ((eq - min) / range) * 200;
                    const line = e.currentTarget.querySelector('.eq-crosshair-line') as HTMLElement;
                    const dot = e.currentTarget.querySelector('.eq-crosshair-dot') as HTMLElement;
                    const tip = e.currentTarget.querySelector('.eq-crosshair-tip') as HTMLElement;
                    if (line) { line.style.left = `${pct * 100}%`; line.style.display = 'block'; }
                    if (dot) { dot.style.left = `${pct * 100}%`; dot.style.top = `${dotY}px`; dot.style.display = 'block'; }
                    if (tip) {
                      const dateStr = date ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
                      tip.textContent = `${dateStr}  $${eq.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
                      tip.style.display = 'block';
                      tip.style.left = pct > 0.7 ? `${pct * 100 - 1}%` : `${pct * 100 + 1}%`;
                      tip.style.textAlign = pct > 0.7 ? 'right' : 'left';
                    }
                  }}
                  onMouseLeave={(e) => {
                    const line = e.currentTarget.querySelector('.eq-crosshair-line') as HTMLElement;
                    const dot = e.currentTarget.querySelector('.eq-crosshair-dot') as HTMLElement;
                    const tip = e.currentTarget.querySelector('.eq-crosshair-tip') as HTMLElement;
                    if (line) line.style.display = 'none';
                    if (dot) dot.style.display = 'none';
                    if (tip) tip.style.display = 'none';
                  }}
                >
                  <svg viewBox="0 0 600 240" preserveAspectRatio="none" style={{ width: '100%', height: 240 }}>
                    <defs>
                      <linearGradient id="btGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22c55e" stopOpacity={0.2} />
                        <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <path d={svg.line} stroke="#22c55e" strokeWidth="2" fill="none" />
                    <path d={svg.area} fill="url(#btGrad)" />
                  </svg>
                  <div className="eq-crosshair-line" style={{ display: 'none', position: 'absolute', top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,0.3)', pointerEvents: 'none' }} />
                  <div className="eq-crosshair-dot" style={{ display: 'none', position: 'absolute', width: 8, height: 8, borderRadius: '50%', background: '#22c55e', border: '2px solid #fff', transform: 'translate(-50%, -50%)', pointerEvents: 'none' }} />
                  <div className="eq-crosshair-tip" style={{ display: 'none', position: 'absolute', top: 6, fontSize: 11, color: '#d4d4d8', fontFamily: 'monospace', whiteSpace: 'nowrap', pointerEvents: 'none' }} />
                </div>
                {/* Equity curve axis labels */}
                {r.equity_curve && Array.isArray(r.equity_curve) && r.equity_curve.length >= 2 && (() => {
                  const vals = r.equity_curve.map((p: any) => typeof p === 'number' ? p : p.equity || p.value || 0);
                  const start = vals[0];
                  const end = vals[vals.length - 1];
                  return (
                    <div className="flex justify-between text-xs" style={{ color: '#52525b', marginTop: 4 }}>
                      <span>${start.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                      <span style={{ color: end >= start ? '#22c55e' : '#ef4444' }}>${end.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Per-Coin Results */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">Per-Coin Results <Tip text="Dollar-weighted P&L contribution per coin as a percentage of starting capital. Shows which coins drove returns and which dragged." /></div>
              </div>
              <div className="card-body">
                {perCoin.length > 0 ? perCoin.map((c: any) => {
                  const pct = c.total_pnl_pct || c.pct || 0;
                  const label = c.symbol?.replace('-USD', '') || c.coin || '?';
                  const color = pct >= 0 ? '#22c55e' : '#ef4444';
                  return (
                    <div key={label} className="coin-perf-bar">
                      <span className="coin-perf-name">{label}</span>
                      <div className="coin-perf-track">
                        <div className="coin-perf-fill" style={{ width: `${Math.abs(pct) / maxCoinPct * 100}%`, background: color }} />
                      </div>
                      <span className="coin-perf-val" style={{ color }}>{pct >= 0 ? '+' : ''}{pct.toFixed(0)}%</span>
                    </div>
                  );
                }) : (
                  <div className="empty-state">
                    <div className="empty-state-text">No per-coin data</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Trade Timeline */}
          {r.trade_timeline && r.trade_timeline.length > 0 && (() => {
            const tl = r.trade_timeline as Array<{ symbol: string; mode: string; entry_date: string; exit_date: string; pnl_pct: number; win: boolean }>;
            const allDates = tl.flatMap(t => [t.entry_date, t.exit_date]);
            const minDate = new Date(allDates.reduce((a, b) => a < b ? a : b));
            const maxDate = new Date(allDates.reduce((a, b) => a > b ? a : b));
            const totalDays = Math.max(1, (maxDate.getTime() - minDate.getTime()) / 86400000);

            const modeOrder = ['SPOT_LONG', 'FUTURES_LONG', 'LONG', 'SHORT'];
            const modeLabels: Record<string, string> = { SPOT_LONG: 'Spot Long', FUTURES_LONG: 'Futures Long', SHORT: 'Short', LONG: 'Long' };
            const modeColors: Record<string, { bar: string; barWin: string; barLoss: string }> = {
              SPOT_LONG: { bar: '#22c55e', barWin: '#22c55e', barLoss: '#ef4444' },
              FUTURES_LONG: { bar: '#3b82f6', barWin: '#3b82f6', barLoss: '#ef4444' },
              SHORT: { bar: '#f97316', barWin: '#f97316', barLoss: '#ef4444' },
              LONG: { bar: '#22c55e', barWin: '#22c55e', barLoss: '#ef4444' },
            };

            const activeModes = modeOrder.filter(m => tl.some(t => t.mode === m));
            const legendH = 24;
            const rowH = 20;
            const gapH = 6;
            const labelW = 90;
            const chartW = 1100;
            const dateAxisH = 24;
            const chartTop = legendH;
            const totalH = legendH + activeModes.length * (rowH + gapH) + dateAxisH;

            // Monthly date ticks
            const monthTicks: { label: string; x: number }[] = [];
            const startMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
            const d = new Date(startMonth);
            while (d <= maxDate) {
              const dayOffset = (d.getTime() - minDate.getTime()) / 86400000;
              if (dayOffset >= 0 && dayOffset <= totalDays) {
                const x = labelW + (dayOffset / totalDays) * chartW;
                const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                const lbl = d.getMonth() === 0 ? `${months[0]} ${d.getFullYear()}` : months[d.getMonth()];
                monthTicks.push({ label: lbl, x });
              }
              d.setMonth(d.getMonth() + 1);
            }
            // Thin out if too many (show every 2nd or 3rd month)
            const maxTicks = Math.floor(chartW / 60);
            const tickStep = monthTicks.length > maxTicks ? Math.ceil(monthTicks.length / maxTicks) : 1;
            const visibleTicks = monthTicks.filter((_, i) => i % tickStep === 0);

            return (
              <div className="card" style={{ marginTop: 20 }}>
                <div className="card-header">
                  <div className="card-title">Trade Timeline <Tip text="Each horizontal bar represents one trade. Colored bars = winning trades, red bars = losing trades. Gaps between bars show periods with no open positions. Hover over any bar to see the coin, dates, and P&L." /></div>
                </div>
                <div className="card-body" style={{ padding: '12px 20px 16px' }}>
                  <svg viewBox={`0 0 ${labelW + chartW} ${totalH}`} style={{ width: '100%', height: 'auto' }} preserveAspectRatio="xMinYMin meet">
                    {/* Legend */}
                    {activeModes.map((mode, mi) => {
                      const colors = modeColors[mode] || modeColors.SPOT_LONG;
                      const lx = mi * 220;
                      return (
                        <g key={`legend-${mode}`}>
                          <rect x={labelW + lx} y={2} width={12} height={12} fill={colors.barWin} rx={2} />
                          <text x={labelW + lx + 16} y={12} fontSize={10} fill="#a1a1aa" fontFamily="system-ui">{modeLabels[mode]} (win)</text>
                          <rect x={labelW + lx + 110} y={2} width={12} height={12} fill={colors.barLoss} rx={2} />
                          <text x={labelW + lx + 126} y={12} fontSize={10} fill="#a1a1aa" fontFamily="system-ui">(loss)</text>
                        </g>
                      );
                    })}
                    {/* Rows */}
                    {activeModes.map((mode, mi) => {
                      const y = chartTop + mi * (rowH + gapH);
                      const modeTrades = tl.filter(t => t.mode === mode);
                      const colors = modeColors[mode] || modeColors.SPOT_LONG;
                      return (
                        <g key={mode}>
                          <text x={0} y={y + rowH / 2 + 4} fontSize={11} fill="#71717a" fontFamily="system-ui">{modeLabels[mode] || mode}</text>
                          <rect x={labelW} y={y} width={chartW} height={rowH} fill="#1e2028" rx={3} />
                          {/* Vertical grid lines */}
                          {visibleTicks.map((tick, ti) => (
                            <line key={`grid-${mi}-${ti}`} x1={tick.x} y1={y} x2={tick.x} y2={y + rowH} stroke="#2a2d35" strokeWidth={0.5} />
                          ))}
                          {modeTrades.map((t, ti) => {
                            const startX = labelW + ((new Date(t.entry_date).getTime() - minDate.getTime()) / 86400000 / totalDays) * chartW;
                            const endX = labelW + ((new Date(t.exit_date).getTime() - minDate.getTime()) / 86400000 / totalDays) * chartW;
                            const w = Math.max(endX - startX, 3);
                            const fill = t.win ? colors.barWin : colors.barLoss;
                            const holdDays = Math.round((new Date(t.exit_date).getTime() - new Date(t.entry_date).getTime()) / 86400000);
                            const tooltip = `${t.symbol} | ${t.entry_date} → ${t.exit_date} (${holdDays}d) | ${t.pnl_pct >= 0 ? '+' : ''}${t.pnl_pct.toFixed(1)}%`;
                            return <rect key={ti} x={startX} y={y + 2} width={w} height={rowH - 4} fill={fill} rx={2} opacity={0.85}><title>{tooltip}</title></rect>;
                          })}
                        </g>
                      );
                    })}
                    {/* Date axis */}
                    {visibleTicks.map((tick, i) => (
                      <g key={`tick-${i}`}>
                        <line x1={tick.x} y1={chartTop} x2={tick.x} y2={chartTop + activeModes.length * (rowH + gapH) - gapH} stroke="#2a2d35" strokeWidth={0.5} strokeDasharray="2,2" />
                        <text x={tick.x} y={chartTop + activeModes.length * (rowH + gapH) + 12} fontSize={9} fill="#52525b" fontFamily="system-ui" textAnchor="middle">{tick.label}</text>
                      </g>
                    ))}
                    {/* Crosshair date cursor */}
                    <g className="tl-crosshair" visibility="hidden">
                      <line className="tl-ch-line" y1={chartTop} y2={chartTop + activeModes.length * (rowH + gapH) - gapH} stroke="#a1a1aa" strokeWidth={0.5} strokeDasharray="3,3" />
                      <rect className="tl-ch-bg" y={chartTop + activeModes.length * (rowH + gapH) + 1} height={14} fill="#27272a" stroke="#3f3f46" strokeWidth={0.5} rx={2} />
                      <text className="tl-ch-text" y={chartTop + activeModes.length * (rowH + gapH) + 12} fontSize={9} fill="#e4e4e7" fontFamily="monospace" textAnchor="middle" />
                    </g>
                    {/* Invisible overlay for mouse tracking */}
                    <rect
                      x={labelW} y={chartTop}
                      width={chartW} height={activeModes.length * (rowH + gapH)}
                      fill="transparent"
                      style={{ cursor: 'crosshair' }}
                      onMouseMove={(evt) => {
                        const svg = evt.currentTarget.closest('svg')!;
                        const rect = svg.getBoundingClientRect();
                        const vbW = labelW + chartW;
                        const svgX = ((evt.clientX - rect.left) / rect.width) * vbW;
                        const cx = Math.max(labelW, Math.min(labelW + chartW, svgX));
                        const dayOff = ((cx - labelW) / chartW) * totalDays;
                        const hoverDate = new Date(minDate.getTime() + dayOff * 86400000);
                        const mo = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                        const label = `${mo[hoverDate.getMonth()]} ${hoverDate.getDate()}, ${hoverDate.getFullYear()}`;
                        const ch = svg.querySelector('.tl-crosshair') as SVGGElement;
                        ch.setAttribute('visibility', 'visible');
                        const ln = ch.querySelector('.tl-ch-line') as SVGLineElement;
                        ln.setAttribute('x1', String(cx)); ln.setAttribute('x2', String(cx));
                        const tx = ch.querySelector('.tl-ch-text') as SVGTextElement;
                        tx.setAttribute('x', String(cx)); tx.textContent = label;
                        const bg = ch.querySelector('.tl-ch-bg') as SVGRectElement;
                        const bw = label.length * 5.5 + 8;
                        bg.setAttribute('x', String(cx - bw / 2)); bg.setAttribute('width', String(bw));
                      }}
                      onMouseLeave={(evt) => {
                        const svg = evt.currentTarget.closest('svg')!;
                        (svg.querySelector('.tl-crosshair') as SVGGElement).setAttribute('visibility', 'hidden');
                      }}
                    />
                  </svg>
                </div>
              </div>
            );
          })()}
        </>
      ) : (
        <div className="card">
          <div className="card-body">
            <div className="empty-state">
              <div className="empty-state-icon">&#9736;</div>
              <div className="empty-state-text">{running ? 'Running backtest...' : 'No backtest results yet'}</div>
              <div className="empty-state-sub">
                {running ? 'Fetching 4 years of daily candles and running the Donchian strategy. First run may take up to 60 seconds.' : 'Configure parameters above and click "Run Backtest" to see results.'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Previous Results — only show completed runs */}
      {completedResults.length > 1 && (
        <div className="card" style={{ marginTop: 20 }}>
          <div className="card-header">
            <div className="card-title">Previous Backtests{totalResults > 0 ? ` (${totalResults})` : ''}</div>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {completedResults.map((item, idx) => {
                const isActive = active?.id === item.id;
                const ret = item.total_return ?? 0;
                const retColor = ret >= 0 ? '#22c55e' : '#ef4444';
                const mode = MODE_LABELS[item.params?.mode] || 'Combined';
                const periodLabel = formatPeriodLabel(item.params);
                return (
                  <div
                    key={item.id}
                    onClick={() => setActive(item)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      alignItems: 'center',
                      gap: 16,
                      padding: '12px 20px',
                      cursor: 'pointer',
                      background: isActive ? 'rgba(34, 197, 94, 0.05)' : idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                      borderLeft: isActive ? '3px solid #22c55e' : '3px solid transparent',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { if (!isActive) (e.currentTarget.style.background = 'rgba(255,255,255,0.03)'); }}
                    onMouseLeave={e => { if (!isActive) (e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)'); }}
                  >
                    {/* Left side: mode, period, date, params */}
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#3f3f46' }}>{shortId(item.id)}</span>
                        {(() => { const b = getStrategyBadge(item.params); return (
                          <span className="badge" style={{ fontSize: 10, padding: '1px 6px', background: b.bg, color: b.fg }}>{b.label}</span>
                        ); })()}
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#e4e4e7' }}>{mode}</span>
                        <span style={{ fontSize: 11, color: '#52525b' }}>&middot;</span>
                        <span style={{ fontSize: 11, color: '#71717a' }}>{periodLabel}</span>
                        <span style={{ fontSize: 11, color: '#52525b' }}>&middot;</span>
                        <span style={{ fontSize: 11, color: '#52525b' }}>{new Date(item.created_at).toLocaleDateString()}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 16, fontSize: 11, color: '#52525b', flexWrap: 'wrap' }}>
                        {item.params?.signal_method ? (
                          <>
                            <span>{(item.params?.signal_timeframe ?? '1d').toUpperCase()}</span>
                            <span>Conf &ge;{item.params?.signal_confidence_gate ?? 50}</span>
                            {item.params?.signal_method === 'smc' && <>
                              <span>OB {item.params?.smc_ob_proximity_pct ?? 1.5}%</span>
                              <span>ADX &ge;{item.params?.smc_adx_threshold ?? 25}</span>
                              <span>SL {item.params?.smc_sl_atr_mult ?? 1.5}x ATR</span>
                            </>}
                            {item.params?.signal_method === 'priceaction' && <>
                              <span>S/R {item.params?.pa_sr_cluster_pct ?? 0.8}%</span>
                              <span>ADX &ge;{item.params?.pa_adx_threshold ?? 25}</span>
                              <span>Dist {item.params?.pa_distance_gate_pct ?? 7.0}%</span>
                              <span>SL {item.params?.pa_sl_atr_mult ?? 1.5}x ATR</span>
                            </>}
                          </>
                        ) : (item.params?.strategy || 'donchian') === 'ualgo' ? (
                          <>
                            <span>ST {item.params?.supertrend_multiplier ?? 2.0}x</span>
                            <span>SL {item.params?.sl_atr_mult ?? 2.0}x ATR</span>
                            <span>Flip {item.params?.flip_window ?? 5}</span>
                            <span>RSI &lt;{item.params?.rsi_buy_threshold ?? 60} &gt;{item.params?.rsi_sell_threshold ?? 40}</span>
                            <span>CMO &gt;{item.params?.cmo_buy_threshold ?? -10} &lt;{item.params?.cmo_sell_threshold ?? 10}</span>
                          </>
                        ) : (
                          <>
                            {item.params?.mode !== 'futures-short' && <span>ATR {item.params?.atr_mult ?? '—'}x</span>}
                            {item.params?.mode !== 'futures-short' && <span>Vol {item.params?.volume_mult ?? '—'}x</span>}
                            {(item.params?.mode === 'combined' || item.params?.mode === 'futures-combined' || item.params?.mode === 'futures-long') && (item.params?.long_leverage ?? 1) > 1 && <span>L-Lev {item.params?.long_leverage}x</span>}
                            {(item.params?.mode === 'combined' || item.params?.mode === 'futures-combined' || item.params?.mode === 'futures-short') && <span>S-ATR {item.params?.short_atr_mult ?? 2.0}x</span>}
                            {(item.params?.mode === 'combined' || item.params?.mode === 'futures-combined' || item.params?.mode === 'futures-short') && <span>S-Vol {item.params?.short_volume_mult ?? 2.0}x</span>}
                            {(item.params?.mode === 'combined' || item.params?.mode === 'futures-combined' || item.params?.mode === 'futures-short') && (item.params?.short_leverage ?? 1) > 1 && <span>S-Lev {item.params?.short_leverage}x</span>}
                            <span>Pyramid {item.params?.pyramid_trigger ?? '—'}%</span>
                            <span>Risk {item.params?.risk_per_trade ?? '—'}%</span>
                          </>
                        )}
                        {!item.params?.signal_method && item.params?.mode !== 'futures-short' && (() => {
                          const bf = item.params?.bull_filter === true ? 'sma200' : (item.params?.bull_filter || 'sma200');
                          const label = BULL_FILTER_LABELS[bf] || bf;
                          const extra = (bf === 'adx_dmi' || bf === 'adx_sma200') ? `≥${item.params?.bull_adx_threshold ?? 25}` : bf === 'supertrend' ? `×${item.params?.bull_supertrend_mult ?? 3}` : '';
                          return <span style={{ color: '#60a5fa' }}>Bull: {label}{extra}</span>;
                        })()}
                        {!item.params?.signal_method && (item.params?.mode === 'combined' || item.params?.mode === 'futures-combined' || item.params?.mode === 'futures-short') && (() => {
                          const bf = item.params?.bear_filter || 'death_cross';
                          const label = BEAR_FILTER_LABELS[bf] || bf;
                          const extra = (bf === 'adx_dmi' || bf === 'adx_sma200') ? `≥${item.params?.bear_adx_threshold ?? 25}` : bf === 'supertrend' ? `×${item.params?.bear_supertrend_mult ?? 3}` : '';
                          return <span style={{ color: '#eab308' }}>Bear: {label}{extra}</span>;
                        })()}
                      </div>
                    </div>
                    {/* Right side: key metrics */}
                    <div style={{ display: 'flex', gap: 20, alignItems: 'center', whiteSpace: 'nowrap' }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 15, fontWeight: 600, fontFamily: 'monospace', color: retColor }}>
                          {ret >= 0 ? '+' : ''}{ret.toFixed(1)}%
                        </div>
                        <div style={{ fontSize: 10, color: '#52525b' }}>Return</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontFamily: 'monospace', color: '#a1a1aa' }}>
                          {item.win_rate?.toFixed(0)}%
                        </div>
                        <div style={{ fontSize: 10, color: '#52525b' }}>WR</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontFamily: 'monospace', color: (item.profit_factor ?? 0) >= 1.5 ? '#22c55e' : '#a1a1aa' }}>
                          {item.profit_factor?.toFixed(2)}
                        </div>
                        <div style={{ fontSize: 10, color: '#52525b' }}>PF</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontFamily: 'monospace', color: '#eab308' }}>
                          -{item.max_drawdown?.toFixed(1)}%
                        </div>
                        <div style={{ fontSize: 10, color: '#52525b' }}>DD</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontFamily: 'monospace', color: '#71717a' }}>
                          {item.total_trades}
                        </div>
                        <div style={{ fontSize: 10, color: '#52525b' }}>Trades</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Pagination */}
          {totalResults > PAGE_SIZE && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: '1px solid #27272a' }}>
              <span className="text-xs" style={{ color: '#71717a' }}>
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalResults)} of {totalResults}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" style={{ padding: '4px 12px', fontSize: 12 }} disabled={page === 0} onClick={() => loadResults(page - 1)}>Prev</button>
                <button className="btn" style={{ padding: '4px 12px', fontSize: 12 }} disabled={(page + 1) * PAGE_SIZE >= totalResults} onClick={() => loadResults(page + 1)}>Next</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Backtest;
